import { Plus, X } from 'lucide-react';
import { ChampDate, type SortieChamp } from './ChampDate';
import { plage, type DateIso } from './dates';
import { libellePeriode, nomPeriode, periodes, rangPeriodeDe } from './selection';
import { FOCUS_ATTR } from './constantes';
import { pluriel } from './textesDemande';
import type { Nd } from './useNouvelleDemande';

const focaliser = (fid: string) => document.querySelector<HTMLElement>(`[${FOCUS_ATTR}="${fid}"]`)?.focus();

/**
 * « Quand ? » : une ligne Du / Au par période. Ces lignes SONT les jours choisis dans le calendrier : en changer une change le calendrier tout de suite,
 * et inversement. Une date tapée au clavier n'est appliquée qu'une fois complète et validée (voir ChampDate).
 */
export function LignesPeriodes({ nd }: { nd: Nd }) {
  const { etat, dispatch, aujourdhui, seancesChoisies, etatCourant, apresRendu, dire, donnees } = nd;
  const pers = periodes(etat.jours);
  const nouvelle = pers.length === 0 || etat.nouvellePeriode.ouverte;
  const saisieRefusee = () => dispatch({ type: 'message', texte: "Cette date est vide, incomplète ou n'existe pas : la période n'a pas changé." });
  const nSeances = (p: [DateIso, DateIso]) => seancesChoisies.filter((s) => s.date >= p[0] && s.date <= p[1]).length;

  /** Une date de période est validée. Le focus suit la période (elle a pu changer de rang après une fusion) : après « Du », on propose « Au ». */
  const surPeriode = (index: number | null, bout: 'du' | 'au', v: DateIso, du: DateIso | '', au: DateIso | '', sortie: boolean, depuis?: SortieChamp) => {
    const nouveauDu = bout === 'du' ? v : du;
    const nouveauAu = bout === 'au' ? v : au;
    if (index == null) dispatch({ type: 'brouillonNouvellePeriode', du: nouveauDu, au: nouveauAu });
    // Un clic sur ‹ › : le calendrier ne saute pas d'abord vers la date tapée (le clic ferait alors DEUX pas de mois) ; il part du mois qu'on voit.
    dispatch({ type: 'periode', index, du: nouveauDu, au: nouveauAu, bout, voir: !(sortie && depuis?.navigationMois) });
    if (sortie) {
      // La date est validée parce qu'on a quitté le champ (Tab, clic) : le focus est déjà où la personne l'a voulu — sauf si c'était un champ de la ligne vide : cette ligne
      // est REMPLACÉE par celle de la période créée, le champ visé disparaît et le focus tomberait sur la page. Il suit alors vers « Au » de la nouvelle période.
      const suite = depuis?.suite;
      if (index == null && suite?.matches(`[${FOCUS_ATTR}="pdun"], [${FOCUS_ATTR}="paun"]`)) {
        apresRendu(() => { const k = rangPeriodeDe(etatCourant().jours, nouveauDu || nouveauAu); if (k >= 0 && !suite.isConnected) focaliser(`pau${k}`); });
      }
      return;
    }
    apresRendu(() => {
      const e = etatCourant();
      const k = rangPeriodeDe(e.jours, nouveauDu || nouveauAu);
      if (k >= 0) focaliser(`pau${k}`); else focaliser(index == null ? (bout === 'du' ? 'paun' : 'pdun') : `p${bout}${index}`);
    });
  };

  return (
    <div className="nd-periodes" role="group" aria-labelledby="nd-h-dates" aria-describedby="nd-per-aide">
      <div className="nd-per-tete" aria-hidden="true"><span>Du</span><span>Au</span><span /></div>
      {pers.map((p, i) => (
        <div className="nd-per" role="group" aria-label={`Période ${i + 1}, ${nomPeriode(p)}`} key={i}>
          <ChampDate
            valeur={p[0]} min={aujourdhui} aria-label={`Période ${i + 1} : du`} data-fid={`pdu${i}`} onRefus={saisieRefusee}
            onValider={(v, sortie, depuis) => surPeriode(i, 'du', v, p[0], p[1], sortie, depuis)}
          />
          <ChampDate
            valeur={p[1]} min={p[0]} aria-label={`Période ${i + 1} : au`} data-fid={`pau${i}`} onRefus={saisieRefusee}
            onValider={(v, sortie, depuis) => surPeriode(i, 'au', v, p[0], p[1], sortie, depuis)}
          />
          <button
            type="button" className="btn btn-icon btn-sm" title="Retirer cette période" data-fid={`px${i}`}
            aria-label={`Retirer la période ${nomPeriode(p)}`}
            onClick={() => {
              dispatch({ type: 'retirerPeriode', index: i });
              // le focus va sur le « Du » de la période qui prend la place, ou sur la ligne vide s'il n'en reste aucune
              apresRendu(() => focaliser(periodes(etatCourant().jours).length ? `pdu${Math.min(i, periodes(etatCourant().jours).length - 1)}` : 'pdun'));
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
          <span className="nd-per-info">
            {libellePeriode(p)} · {pluriel(plage(p[0], p[1], true).length, 'jour')}
            {etat.absentId != null && donnees.etat === 'pretes' && ` · ${nSeances(p) ? pluriel(nSeances(p), 'séance') : 'pas de séance'}`}
          </span>
        </div>
      ))}
      {nouvelle && (
        <div className="nd-per nd-per--nouvelle" role="group" aria-label={pers.length ? 'Nouvelle période' : 'Période'}>
          <ChampDate
            valeur={etat.nouvellePeriode.du} min={aujourdhui} aria-label={`${pers.length ? 'Nouvelle période' : 'Période'} : du`} data-fid="pdun"
            onValider={(v, sortie, depuis) => surPeriode(null, 'du', v, etat.nouvellePeriode.du, etat.nouvellePeriode.au, sortie, depuis)}
          />
          <ChampDate
            valeur={etat.nouvellePeriode.au} min={etat.nouvellePeriode.du || aujourdhui}
            aria-label={`${pers.length ? 'Nouvelle période' : 'Période'} : au (vide : un seul jour)`} data-fid="paun"
            onValider={(v, sortie, depuis) => surPeriode(null, 'au', v, etat.nouvellePeriode.du, etat.nouvellePeriode.au, sortie, depuis)}
          />
          {pers.length ? (
            <button type="button" className="btn btn-icon btn-sm" title="Annuler" aria-label="Annuler la nouvelle période" data-fid="pxn"
              onMouseDown={(e) => e.preventDefault()}   /* le champ garde le focus : sa date tapée n'est pas validée par la sortie, la ligne se ferme et la jette */
              onClick={() => { dispatch({ type: 'retirerPeriode', index: 'nouvelle' }); apresRendu(() => focaliser('perAjout')); }}>
              <X size={14} aria-hidden="true" />
            </button>
          ) : <span aria-hidden="true" />}
        </div>
      )}
      {!nouvelle && (
        <button type="button" className="nd-lien" data-fid="perAjout" onClick={() => { dispatch({ type: 'ouvrirNouvellePeriode' }); apresRendu(() => focaliser('pdun')); }}>
          <Plus size={14} aria-hidden="true" />Ajouter une période
        </button>
      )}
      {etat.messageDates && <p className="nd-per-msg" role="status"><span>{etat.messageDates}</span></p>}
      <p className="nd-aide" id="nd-per-aide">
        {etat.jours.length
          ? dire("Ce que vous choisissez dans le calendrier s'écrit ici, et inversement.", "Ce qui est choisi dans le calendrier s'écrit ici, et inversement.")
          : 'Cliquez ou glissez sur les jours dans le calendrier, ou saisissez une période ici : les deux restent synchronisés.'}
      </p>
      {etat.annulation && (
        <p className="nd-aide nd-annuler">
          {etat.annulation.texte}{' '}
          <button type="button" className="nd-lien nd-lien--ligne" data-fid="annulerRetrait" onClick={() => { dispatch({ type: 'annulerRetrait' }); apresRendu(() => focaliser('perAjout')); }}>Annuler</button>
        </p>
      )}
    </div>
  );
}
